import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import propTypes from "@styled-system/prop-types";
import { useController, useForm } from "react-hook-form";
import { nanoid } from "nanoid";
import { pick, omit } from "lodash";
import {
  FormInputWrapper,
  FormLabelStyle,
  FormInputGroupStyle,
  FormInputGroupAppendStyle,
  FormInputStyle,
  FormHelpStyle,
  FormInvalidFeedbackStyle,
} from "./forms.style";
import Button from "../button";
import SpiMasker from "../../../utilities/spiMasker";

const FormInput = ({
  innerRef,
  validationRules,
  control: controlProp,
  formState: formStateProp,
  plainText,
  inline,
  label,
  type,
  name,
  defaultValue,
  id,
  helpText,
  spiMask,
  timeout,
  onInput,
  onChange,
  onBlur,
  appendButton,
  buttonText,
  buttonColor,
  buttonLoading,
  buttonDisabled,
  onButtonClick,
  ...props
}) => {
  const formHook = useForm();
  const control = controlProp || formHook.control;
  const formState = formStateProp || formHook.formState;
  const [showMaskedValue, setShowMaskedValue] = useState(!!spiMask);
  const [isRequired] = useState(!!validationRules?.required);
  const [error, setError] = useState(formState?.errors[name] ?? undefined);
  const [showTimeout, saveTimeout] = useState();
  const maskedInput = useRef(null);
  const TIMEOUT_MS = 200;
  const inputId = id || `input_${nanoid()}`;
  const spacePropsKeys = Object.keys(propTypes.space);
  const spaceProps = pick(props, spacePropsKeys);
  const attributes = omit(props, spacePropsKeys);
  const {
    field: { onChange: oc, onBlur: ob, value, ref: originalInput },
    meta: { invalid },
  } = useController({
    name,
    control,
    rules: validationRules,
    defaultValue: defaultValue || "",
    onFocus() {
      if (originalInput.current instanceof Element) {
        originalInput.current.focus();
      }
    }
  });

  /* Set field error */
  useEffect(() => {
    if (!formState?.errors) {
      setError(undefined);
    } else if (formState.errors[name] !== error) {
      setError(formState.errors[name]);
    }
  }, [formState, name, error]);

  const maskValue = (val) => {
    if (!SpiMasker[spiMask]) {
      return val;
    }

    return SpiMasker[spiMask](val);
  };

  const displayMaskedInput = () => {
    const localTimeout = setTimeout(() => {
      setShowMaskedValue(true);
      clearTimeout(localTimeout);
      saveTimeout(null);
    }, timeout || TIMEOUT_MS);
    saveTimeout(localTimeout);
  };

  const hideMaskedInput = () => {
    if (showTimeout) {
      clearTimeout(showTimeout);
      saveTimeout(null);
    }

    setShowMaskedValue(false);
  };

  const onMaskedInputFocus = () => {
    hideMaskedInput();
    setTimeout(() => {
      originalInput.current.focus();
    }, 10);
  };

  const onInputChange = (e) => {
    oc(e);
    onChange && onChange(e);
  };

  const onInputBlur = (e) => {
    ob();

    if (spiMask) {
      displayMaskedInput();
    }

    onBlur && onBlur(e);
  };

  const renderInput = () => (
    <>
      {spiMask && showMaskedValue && (
        <FormInputStyle
          ref={maskedInput}
          plainText={plainText}
          type={type}
          name={name}
          id={`spi-masked-${inputId}`}
          placeholder={attributes.placeholder || ""}
          defaultValue={maskValue(value)}
          invalid={invalid}
          onFocus={onMaskedInputFocus}
        />
      )}
      {(!spiMask || !showMaskedValue) && (
        <FormInputStyle
          ref={originalInput}
          plainText={plainText}
          type={type}
          name={name}
          id={inputId}
          value={value}
          invalid={invalid}
          onInput={onInput}
          onChange={onInputChange}
          onBlur={onInputBlur}
          mt={0}
          {...attributes}
        />
      )}
    </>
  );

  const renderInputHelpers = () => (
    <>
      {helpText && <FormHelpStyle>{helpText}</FormHelpStyle>}
      {error && <FormInvalidFeedbackStyle role="alert">{error.message}</FormInvalidFeedbackStyle>}
    </>
  );

  return (
    <FormInputWrapper {...spaceProps} ref={innerRef} inline={inline}>
      {label && <FormLabelStyle htmlFor={inputId}>{label}{isRequired && ` *`}</FormLabelStyle>}
      {!appendButton && renderInput()}
      {appendButton && (
        <FormInputGroupStyle>
          {renderInput()}
          <FormInputGroupAppendStyle>
            <Button type="button" colors={buttonColor} loading={buttonLoading}
                    disabled={buttonDisabled}
                    onClick={onButtonClick}>{buttonText}</Button>
          </FormInputGroupAppendStyle>
          {renderInputHelpers()}
        </FormInputGroupStyle>
      )}
      {!appendButton && renderInputHelpers()}
    </FormInputWrapper>
  );
};

FormInput.propTypes = {
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  validationRules: PropTypes.object,
  watch: PropTypes.func,
  control: PropTypes.object,
  formState: PropTypes.object,
  plainText: PropTypes.bool,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  type: PropTypes.string,
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  helpText: PropTypes.string,
  spiMask: PropTypes.oneOf(["email", "phone", "dni"]),
  timeout: PropTypes.number,
  onInput: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  appendButton: PropTypes.bool,
  buttonText: PropTypes.string,
  buttonColor: PropTypes.string,
  buttonLoading: PropTypes.bool,
  buttonDisabled: PropTypes.bool,
  onButtonClick: PropTypes.func,
  ...propTypes.space,
};

FormInput.defaultProps = {
  type: "text",
  inline: false,
  disabled: false,
  defaultValue: "",
};

export default FormInput;
